import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabaseClient'
import { useAuth } from '@/features/auth/AuthContext'
import { useNavigate } from 'react-router-dom'
import TeamManagement from '@/features/team/components/TeamManagement'
import AdminUserManagement from '@/components/AdminUserManagement'
import AddressAutocomplete from '@/components/AddressAutocomplete'
import ApiKeySettings from '@/components/ApiKeySettings'

export default function SettingsPage() {
  const { user, profile } = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [activeTab, setActiveTab] = useState('profile')

  const [profileData, setProfileData] = useState({
    full_name: '',
    countryCode: '+64',
    mobileNumber: '',
    address: '',
    buyer_intent: '',
    buyer_category: '',
  })

  const countryCodes = [
    { code: '+64', country: 'New Zealand', keywords: ['new zealand', 'nz', 'auckland', 'wellington', 'christchurch'] },
    { code: '+61', country: 'Australia', keywords: ['australia', 'au', 'sydney', 'melbourne', 'brisbane'] },
    { code: '+1', country: 'USA/Canada', keywords: ['usa', 'united states', 'america', 'canada', 'us'] },
    { code: '+44', country: 'UK', keywords: ['uk', 'united kingdom', 'britain', 'england', 'london'] },
    { code: '+65', country: 'Singapore', keywords: ['singapore'] },
    { code: '+852', country: 'Hong Kong', keywords: ['hong kong', 'hk'] },
    { code: '+86', country: 'China', keywords: ['china', 'beijing', 'shanghai'] },
    { code: '+91', country: 'India', keywords: ['india', 'mumbai', 'delhi'] },
    { code: '+81', country: 'Japan', keywords: ['japan', 'tokyo'] },
    { code: '+82', country: 'South Korea', keywords: ['korea', 'seoul'] },
  ]

  const detectCountryFromAddress = (address) => {
    if (!address) return '+64'
    const lowerAddress = address.toLowerCase()
    for (const country of countryCodes) {
      if (country.keywords.some(keyword => lowerAddress.includes(keyword))) {
        return country.code
      }
    }
    return '+64'
  }

  const parseExistingPhone = (phone) => {
    if (!phone) return { countryCode: '+64', mobileNumber: '' }
    const match = phone.match(/^(\+\d{1,4})\s*(.*)$/)
    if (match) {
      const foundCode = countryCodes.find(c => c.code === match[1])
      return {
        countryCode: foundCode ? match[1] : '+64',
        mobileNumber: match[2] || ''
      }
    }
    return { countryCode: '+64', mobileNumber: phone }
  }

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  })

  const [mfaEnrolled, setMfaEnrolled] = useState(false)
  const [qrCode, setQrCode] = useState('')
  const [verifyCode, setVerifyCode] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  useEffect(() => {
    if (profile) {
      const { countryCode, mobileNumber } = parseExistingPhone(profile.phone)
      setProfileData({
        full_name: profile.full_name || '',
        countryCode: countryCode,
        mobileNumber: mobileNumber,
        address: profile.address || '',
        buyer_intent: profile.buyer_intent || '',
        buyer_category: profile.buyer_category || '',
      })
    }
  }, [profile])

  useEffect(() => {
    checkMFAStatus()
  }, [])

  const checkMFAStatus = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors()
      if (error) throw error
      const hasVerifiedFactor = data?.totp?.some(factor => factor.status === 'verified')
      setMfaEnrolled(hasVerifiedFactor || false)
    } catch (err) {
      console.error('Error checking MFA status:', err)
    }
  }

  const updateProfileMutation = useMutation({
    mutationFn: async (updatedProfile) => {
      const fullPhone = updatedProfile.mobileNumber
        ? `${updatedProfile.countryCode} ${updatedProfile.mobileNumber}`.trim()
        : ''

      const updateData = {
        full_name: updatedProfile.full_name,
        phone: fullPhone,
        address: updatedProfile.address || null,
        updated_at: new Date().toISOString(),
      }

      if (profile.role === 'buyer') {
        updateData.buyer_intent = updatedProfile.buyer_intent || null
        updateData.buyer_category = updatedProfile.buyer_category || null
      }

      const { data, error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id)
        .select()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['profile'])
      setSuccess('Profile updated successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    },
  })

  const changePasswordMutation = useMutation({
    mutationFn: async (newPassword) => {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      })
      if (error) throw error
    },
    onSuccess: () => {
      setPasswordData({ newPassword: '', confirmPassword: '' })
      setSuccess('Password changed successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    },
  })

  const enrollMFAMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
      })
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      setQrCode(data.totp.qr_code)
    },
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    },
  })

  const verifyMFAMutation = useMutation({
    mutationFn: async ({ factorId, code }) => {
      const challenge = await supabase.auth.mfa.challenge({ factorId })
      if (challenge.error) throw challenge.error

      const verify = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.data.id,
        code,
      })
      if (verify.error) throw verify.error
      return verify.data
    },
    onSuccess: () => {
      setMfaEnrolled(true)
      setQrCode('')
      setVerifyCode('')
      setSuccess('Two-factor authentication enabled successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    },
  })

  const unenrollMFAMutation = useMutation({
    mutationFn: async (factorId) => {
      const { error } = await supabase.auth.mfa.unenroll({ factorId })
      if (error) throw error
    },
    onSuccess: () => {
      setMfaEnrolled(false)
      setSuccess('Two-factor authentication disabled!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    },
  })

  const deleteAccountMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('No active session')

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-user-account`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to delete account')
      }

      return await response.json()
    },
    onSuccess: () => {
      navigate('/signin')
    },
    onError: (err) => {
      setError(err.message)
      setTimeout(() => setError(''), 5000)
    },
  })

  const handleProfileUpdate = () => {
    if (!profileData.full_name.trim()) {
      setError('Full name is required')
      setTimeout(() => setError(''), 5000)
      return
    }
    updateProfileMutation.mutate(profileData)
  }

  const handlePasswordChange = () => {
    if (passwordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters')
      setTimeout(() => setError(''), 5000)
      return
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Passwords do not match')
      setTimeout(() => setError(''), 5000)
      return
    }
    changePasswordMutation.mutate(passwordData.newPassword)
  }

  const handleEnableMFA = () => {
    enrollMFAMutation.mutate()
  }

  const handleVerifyMFA = async () => {
    const { data } = await supabase.auth.mfa.listFactors()
    const factor = data?.totp?.[0]
    if (factor && verifyCode) {
      verifyMFAMutation.mutate({ factorId: factor.id, code: verifyCode })
    }
  }

  const handleDisableMFA = async () => {
    const { data } = await supabase.auth.mfa.listFactors()
    const factor = data?.totp?.find(f => f.status === 'verified')
    if (factor) {
      unenrollMFAMutation.mutate(factor.id)
    }
  }

  const handleDeleteAccount = () => {
    deleteAccountMutation.mutate()
  }

  return (
    <div className="bg-secondary-50 min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">Settings</h1>
          <p className="text-secondary-600">Manage your account preferences and notification settings</p>
        </div>

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md text-green-700">
            {success}
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 border-b border-secondary-200 bg-white rounded-t-lg overflow-x-auto">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 whitespace-nowrap ${
              activeTab === 'profile'
                ? 'border-primary-600 text-primary-700'
                : 'border-transparent text-secondary-600 hover:text-secondary-900'
            }`}
          >
            Profile
          </button>
          {profile?.role === 'buyer' && (
            <button
              onClick={() => setActiveTab('buyer-preferences')}
              className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 whitespace-nowrap ${
                activeTab === 'buyer-preferences'
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900'
              }`}
            >
              Buyer Preferences
            </button>
          )}
          {profile?.admin_level && (
            <button
              onClick={() => setActiveTab('user-management')}
              className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 whitespace-nowrap ${
                activeTab === 'user-management'
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900'
              }`}
            >
              User Management
            </button>
          )}
          {profile?.admin_level && (
            <button
              onClick={() => setActiveTab('integrations')}
              className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 whitespace-nowrap ${
                activeTab === 'integrations'
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900'
              }`}
            >
              Integrations
            </button>
          )}
          {profile?.role === 'developer' && (
            <button
              onClick={() => setActiveTab('templates')}
              className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 whitespace-nowrap ${
                activeTab === 'templates'
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900'
              }`}
            >
              Templates
            </button>
          )}
          {profile?.role === 'developer' && (
            <button
              onClick={() => setActiveTab('team')}
              className={`px-6 py-3 font-medium text-sm transition-colors border-b-2 whitespace-nowrap ${
                activeTab === 'team'
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-secondary-600 hover:text-secondary-900'
              }`}
            >
              Team
            </button>
          )}
        </div>

        <div className="space-y-6">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <>
              {/* Profile Information */}
              <div className="card p-6">
                <h2 className="text-xl font-semibold text-secondary-900 mb-4">Profile Information</h2>
            <div className="space-y-4">
              <div>
                <label className="label">Role</label>
                <input
                  type="text"
                  value={profile?.role || ''}
                  disabled
                  className="input bg-secondary-100 cursor-not-allowed capitalize"
                />
              </div>
              <div>
                <label className="label">Email</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="input bg-secondary-100 cursor-not-allowed"
                />
                <p className="text-xs text-secondary-500 mt-1">Email cannot be changed</p>
              </div>
              <div>
                <label className="label">Full Name</label>
                <input
                  type="text"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))}
                  className="input"
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <label className="label">Address</label>
                <AddressAutocomplete
                  value={profileData.address}
                  onChange={(val) => {
                    setProfileData(prev => ({ ...prev, address: val }))
                    const detectedCode = detectCountryFromAddress(val)
                    if (!profileData.mobileNumber) {
                      setProfileData(prev => ({ ...prev, countryCode: detectedCode }))
                    }
                  }}
                  onSelect={(selected) => {
                    const newAddress = selected.placeName || selected.address || ''
                    const detectedCode = detectCountryFromAddress(newAddress)
                    setProfileData(prev => ({
                      ...prev,
                      address: newAddress,
                      countryCode: !prev.mobileNumber ? detectedCode : prev.countryCode,
                    }))
                  }}
                  placeholder="Enter your address"
                />
              </div>
              <div>
                <label className="label">Mobile Number</label>
                <div className="flex gap-2">
                  <select
                    value={profileData.countryCode}
                    onChange={(e) => setProfileData(prev => ({ ...prev, countryCode: e.target.value }))}
                    className="input w-40"
                  >
                    {countryCodes.map(({ code, country }) => (
                      <option key={code} value={code}>
                        {code} ({country})
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={profileData.mobileNumber}
                    onChange={(e) => setProfileData(prev => ({ ...prev, mobileNumber: e.target.value }))}
                    className="input flex-1"
                    placeholder="21 123 4567"
                  />
                </div>
              </div>
              <div className="flex justify-end pt-2">
                <button
                  onClick={handleProfileUpdate}
                  disabled={updateProfileMutation.isPending}
                  className="btn-primary"
                >
                  {updateProfileMutation.isPending ? 'Saving...' : 'Update Profile'}
                </button>
              </div>
            </div>
          </div>

              {/* Security */}
              <div className="card p-6">
                <h2 className="text-xl font-semibold text-secondary-900 mb-4">Security</h2>
            <div className="space-y-6">
              {/* Password Change */}
              <div className="pb-6 border-b border-secondary-200">
                <h3 className="font-medium text-secondary-900 mb-3">Change Password</h3>
                <div className="space-y-3">
                  <div>
                    <label className="label">New Password</label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      className="input"
                      placeholder="Enter new password"
                    />
                  </div>
                  <div>
                    <label className="label">Confirm New Password</label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="input"
                      placeholder="Confirm new password"
                    />
                  </div>
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={handlePasswordChange}
                      disabled={changePasswordMutation.isPending || !passwordData.newPassword}
                      className="btn-primary"
                    >
                      {changePasswordMutation.isPending ? 'Changing...' : 'Change Password'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Two-Factor Authentication */}
              <div>
                <h3 className="font-medium text-secondary-900 mb-3">Two-Factor Authentication</h3>
                {!mfaEnrolled ? (
                  <div className="space-y-3">
                    <p className="text-sm text-secondary-600">
                      Add an extra layer of security to your account by enabling two-factor authentication.
                    </p>
                    {!qrCode ? (
                      <button
                        onClick={handleEnableMFA}
                        disabled={enrollMFAMutation.isPending}
                        className="btn-primary"
                      >
                        {enrollMFAMutation.isPending ? 'Setting up...' : 'Enable Two-Factor Authentication'}
                      </button>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-secondary-700 font-medium">
                          Scan this QR code with your authenticator app:
                        </p>
                        <div className="bg-white p-4 rounded-lg inline-block">
                          <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                        </div>
                        <div>
                          <label className="label">Enter verification code</label>
                          <input
                            type="text"
                            value={verifyCode}
                            onChange={(e) => setVerifyCode(e.target.value)}
                            className="input max-w-xs"
                            placeholder="000000"
                            maxLength={6}
                          />
                        </div>
                        <button
                          onClick={handleVerifyMFA}
                          disabled={verifyMFAMutation.isPending || !verifyCode}
                          className="btn-primary"
                        >
                          {verifyMFAMutation.isPending ? 'Verifying...' : 'Verify and Enable'}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-green-700">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium">Two-factor authentication is enabled</span>
                    </div>
                    <button
                      onClick={handleDisableMFA}
                      disabled={unenrollMFAMutation.isPending}
                      className="btn-secondary text-red-600 border-red-300 hover:bg-red-50"
                    >
                      {unenrollMFAMutation.isPending ? 'Disabling...' : 'Disable Two-Factor Authentication'}
                    </button>
                  </div>
                )}
                </div>
              </div>
            </div>

              {/* Danger Zone */}
              <div className="card p-6 border-2 border-red-200">
                <h2 className="text-xl font-semibold text-red-900 mb-4">Danger Zone</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-red-900">Delete Account</div>
                      <div className="text-sm text-secondary-600">
                        Permanently delete your account and all associated data
                      </div>
                    </div>
                    {!showDeleteConfirm ? (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="btn-secondary text-red-600 border-red-300 hover:bg-red-50"
                      >
                        Delete Account
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          className="btn-secondary"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleDeleteAccount}
                          disabled={deleteAccountMutation.isPending}
                          className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
                        >
                          {deleteAccountMutation.isPending ? 'Deleting...' : 'Confirm Delete'}
                        </button>
                      </div>
                    )}
                  </div>
                  {showDeleteConfirm && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                      <p className="text-sm text-red-800 font-medium">
                        Warning: This action cannot be undone. All your data will be permanently deleted.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Buyer Preferences Tab */}
          {activeTab === 'buyer-preferences' && profile?.role === 'buyer' && (
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-secondary-900 mb-2">Buyer Preferences</h2>
              <p className="text-secondary-600 mb-6">
                Help us personalize your experience by telling us about your buying intentions
              </p>

              <div className="space-y-6">
                <div>
                  <label className="label">How are you planning to use your property?</label>
                  <select
                    value={profileData.buyer_intent}
                    onChange={(e) => setProfileData(prev => ({ ...prev, buyer_intent: e.target.value }))}
                    className="input"
                  >
                    <option value="">Select an option</option>
                    <option value="owner_occupied">Owner-Occupied (I plan to live there)</option>
                    <option value="investment">Investment Property</option>
                  </select>
                  <p className="text-sm text-secondary-500 mt-1">
                    This helps developers understand the buyer mix for their projects
                  </p>
                </div>

                {profileData.buyer_intent === 'owner_occupied' && (
                  <div>
                    <label className="label">Which best describes you?</label>
                    <select
                      value={profileData.buyer_category}
                      onChange={(e) => setProfileData(prev => ({ ...prev, buyer_category: e.target.value }))}
                      className="input"
                    >
                      <option value="">Select an option</option>
                      <option value="first_home">First Home Buyer</option>
                      <option value="immigrant">Recent Immigrant</option>
                      <option value="upgrading">Upgrading</option>
                      <option value="downsizing">Downsizing</option>
                    </select>
                    <p className="text-sm text-secondary-500 mt-1">
                      This may qualify you for special offers and incentives
                    </p>
                  </div>
                )}

                <div className="flex justify-end pt-4 border-t border-secondary-200">
                  <button
                    onClick={handleProfileUpdate}
                    disabled={updateProfileMutation.isPending}
                    className="btn-primary"
                  >
                    {updateProfileMutation.isPending ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* User Management Tab */}
          {activeTab === 'user-management' && profile?.admin_level && (
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-secondary-900 mb-6">User Management</h2>
              <AdminUserManagement />
            </div>
          )}

          {/* Integrations Tab */}
          {activeTab === 'integrations' && profile?.admin_level && (
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-secondary-900 mb-2">API Integrations</h2>
              <p className="text-secondary-600 mb-6">
                Configure system-wide API keys for third-party services
              </p>
              <ApiKeySettings />
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && profile?.role === 'developer' && (
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-secondary-900 mb-4">Email Templates</h2>
              <p className="text-secondary-600 mb-6">
                Customize email templates for notifications and communications.
              </p>
              <div className="space-y-4">
                <div className="border border-secondary-200 rounded-lg p-4">
                  <h3 className="font-medium text-secondary-900 mb-2">Welcome Email</h3>
                  <p className="text-sm text-secondary-600 mb-3">
                    Sent to new users when they sign up
                  </p>
                  <button className="btn-secondary text-sm">Edit Template</button>
                </div>
                <div className="border border-secondary-200 rounded-lg p-4">
                  <h3 className="font-medium text-secondary-900 mb-2">Reservation Confirmation</h3>
                  <p className="text-sm text-secondary-600 mb-3">
                    Sent when a buyer reserves a unit
                  </p>
                  <button className="btn-secondary text-sm">Edit Template</button>
                </div>
                <div className="border border-secondary-200 rounded-lg p-4">
                  <h3 className="font-medium text-secondary-900 mb-2">Reservation Expiry Reminder</h3>
                  <p className="text-sm text-secondary-600 mb-3">
                    Sent 24 hours before reservation expires
                  </p>
                  <button className="btn-secondary text-sm">Edit Template</button>
                </div>
              </div>
              <p className="text-sm text-secondary-500 mt-6">
                Note: Template editing functionality coming soon
              </p>
            </div>
          )}

          {/* Team Tab */}
          {activeTab === 'team' && profile?.role === 'developer' && (
            <div className="card p-6">
              <TeamManagement developerId={profile.id} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
